import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { notificationService } from '../api'
import { useAuth } from '../context/AuthContext'

const NotificationBell = ({ variant = 'dashboard', inboxPath = '/notifications' }) => {
  const { isAuthenticated } = useAuth()
  const [unreadCount, setUnreadCount] = useState(0)
  const [notifications, setNotifications] = useState([])
  const [showNotifs, setShowNotifs] = useState(false)
  const bellRef = useRef(null)

  const fetchUnread = () => {
    notificationService.getUnreadCount()
      .then((res) => setUnreadCount(res.data.count || 0))
      .catch(() => {})
  }

  useEffect(() => {
    if (!isAuthenticated) return undefined
    fetchUnread()
    const interval = setInterval(fetchUnread, 60000)
    return () => clearInterval(interval)
  }, [isAuthenticated])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setShowNotifs(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleBellClick = () => {
    if (!showNotifs) {
      notificationService.getMyNotifications({ page: 1, page_size: 5 })
        .then((res) => setNotifications(res.data.results || res.data))
        .catch(() => {})
    }
    setShowNotifs((prev) => !prev)
  }

  const handleMarkRead = (id) => {
    notificationService.markRead(id).then(() => {
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)))
      setUnreadCount((prev) => Math.max(0, prev - 1))
    }).catch(() => {})
  }

  if (!isAuthenticated) return null

  const isNavbar = variant === 'navbar'

  return (
    <div ref={bellRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={handleBellClick}
        aria-label="Notifications"
        aria-expanded={showNotifs}
        className={isNavbar ? 'hover:shadow-sm' : 'p-sm text-muted hover:text-primary transition-colors relative bg-transparent border-none'}
        style={isNavbar ? {
          position: 'relative',
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: '50%',
          cursor: 'pointer',
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          color: 'var(--color-text-primary)',
        } : { position: 'relative', cursor: 'pointer' }}
      >
        <Bell size={isNavbar ? 18 : 22} color={isNavbar ? 'var(--color-text-primary)' : undefined} />
        {unreadCount > 0 && (
          <span
            className={isNavbar ? undefined : 'absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full border-2 border-white'}
            style={isNavbar ? {
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              backgroundColor: 'var(--color-error)',
              color: 'white',
              borderRadius: '999px',
              fontSize: '0.65rem',
              fontWeight: 700,
              width: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            } : undefined}
          >
            {isNavbar ? (unreadCount > 9 ? '9+' : unreadCount) : null}
          </span>
        )}
      </button>

      {showNotifs && (
        <div
          className="card"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 12px)',
            width: '320px',
            padding: 0,
            zIndex: 1000,
            overflow: 'hidden',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          <div style={{
            padding: '1rem',
            borderBottom: '1px solid var(--color-border)',
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'var(--color-bg-primary)',
            color: 'var(--color-text-primary)',
          }}
          >
            <span>Notifications</span>
            {unreadCount > 0 && (
              <span style={{
                fontSize: '0.75rem',
                color: 'var(--color-primary)',
                backgroundColor: 'var(--color-primary-transparent)',
                padding: '2px 8px',
                borderRadius: '12px',
              }}
              >
                {unreadCount} new
              </span>
            )}
          </div>

          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
                You&apos;re all caught up!
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  style={{
                    padding: '1rem',
                    borderBottom: '1px solid var(--color-border)',
                    backgroundColor: !notif.is_read ? 'var(--color-dashboard-bg)' : 'transparent',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    {notif.subject && (
                      <p style={{ margin: '0 0 4px 0', fontWeight: 600, fontSize: '0.875rem' }}>
                        {notif.subject}
                      </p>
                    )}
                    <p style={{ margin: 0, fontWeight: !notif.is_read ? 600 : 400, fontSize: '0.875rem' }}>
                      {notif.message}
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                      {notif.time_ago || ''}
                    </p>
                  </div>
                  {!notif.is_read && (
                    <button
                      type="button"
                      onClick={() => handleMarkRead(notif.id)}
                      className="btn-ghost"
                      style={{ fontSize: '0.75rem', padding: '4px 8px', borderRadius: '4px', flexShrink: 0 }}
                    >
                      Mark read
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          <Link
            to={inboxPath}
            onClick={() => setShowNotifs(false)}
            style={{
              display: 'block',
              padding: '0.85rem 1rem',
              textAlign: 'center',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--color-primary)',
              borderTop: '1px solid var(--color-border)',
              textDecoration: 'none',
            }}
          >
            View all notifications
          </Link>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
